import { useEffect, useState } from "react";
import { IconX, IconRoute } from "@tabler/icons-react";
import NormalSearch from "../../../components/common/NormalSearch";
import {
  useFetchDriversAllData,
  useAssignRoute,
} from "../../../apis/driverApis";
import { useFetchRouteDetailsData } from "../../../apis/routeApis";
import useDebounce from "../../../utilities/useDebounce";
import toast from "react-hot-toast";

interface Driver {
  id: string;
  name: string;
  routeCodes?: string[];
  profileImage?: string;
}

interface AssignDriversModalProps {
  open: boolean;
  onClose: () => void;
  routeId: string | null;
}

export default function AssignDriversModal({
  open,
  onClose,
  routeId,
}: AssignDriversModalProps) {
  const [search, setSearch] = useState("");
  const [assigningDriverId, setAssigningDriverId] = useState<string | null>(
    null
  );

  const debouncedSearch = useDebounce(search, 500);

  const { data: routeDetails, isPending: isRouteDetailsPending } =
    useFetchRouteDetailsData(routeId ?? "");

  const { data: drivers, refetch: fetchDrivers } =
    useFetchDriversAllData(debouncedSearch);

  const { mutate: assignRoute } = useAssignRoute();

  useEffect(() => {
    if (open) {
      fetchDrivers();
    }
  }, [debouncedSearch, open]);

  const handleAssignRoute = (driver: Driver, action: "Assign" | "Unassign") => {
    setAssigningDriverId(driver.id);
    const currentRouteCodes = driver.routeCodes || [];
    const currentRoute = routeDetails?.routeCode || "";

    const data = {
      id: driver.id,
      routeCodes:
        action === "Assign"
          ? [...currentRouteCodes, currentRoute]
          : currentRouteCodes.filter((r) => r !== currentRoute),
    };

    assignRoute(data, {
      onSuccess: () => {
        toast.success(
          `Driver ${
            action === "Assign" ? "assigned" : "unassigned"
          } successfully`
        );
        fetchDrivers();
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Error in assignment");
      },
      onSettled: () => {
        setAssigningDriverId(null);
      },
    });
  };

  const handleAssignClick = (driver: Driver) => {
    const isAssigned = driver.routeCodes?.includes(
      routeDetails?.routeCode || ""
    );
    handleAssignRoute(driver, isAssigned ? "Unassign" : "Assign");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-[#F7F6F2] rounded-3xl w-[555px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-medium text-[#212121]">Assign Drivers ({drivers?.length || 0})</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full border-[0.5px] border-[#D2D2D2]"
          >
            <IconX size={20} />
          </button>
        </div>

        {/* Search + Filter */}
        <div className="px-4 py-3">
          <NormalSearch
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Drivers List */}
        <div className="flex-1  max-h-[75vh]  overflow-y-auto px-4 space-y-1">
          {isRouteDetailsPending ? (
            <div className="flex justify-center items-center h-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : drivers?.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No drivers found</p>
          ) : (
            drivers?.map((driver: Driver) => {
              const isAssigning = assigningDriverId === driver.id;
              const isDisabled = !!assigningDriverId && !isAssigning;
              const isAssignedToCurrentRoute = driver.routeCodes?.includes(
                routeDetails?.routeCode || ""
              );

              return (
                <div
                  key={driver.id}
                  className={`flex justify-between items-center p-2 rounded-lg transition-colors ${
                    isDisabled ? "opacity-60" : "hover:bg-black/5"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="relative">
                      <img
                        src={driver.profileImage || "/img/dummy-driver.png"}
                        alt={driver.name}
                        className="rounded-full w-10 h-10 object-cover border-2 border-gray-200 flex-shrink-0"
                        onError={(e) =>
                          ((e.target as HTMLImageElement).src =
                            "/img/dummy-driver.png")
                        }
                      />
                      {driver.routeCodes?.length ? (
                        <div className="absolute -bottom-1 -right-1 bg-yellow-100 rounded-full p-1 border-2 border-white">
                          <IconRoute size={12} className="text-yellow-600" />
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1 min-w-0">
                      <h2 className="text-sm font-medium text-[#212121] truncate">
                        {driver.name}
                      </h2>
                      <p className="text-xs text-[#212121]/70 border border-[#D2D2D2] px-2 py-0.5 rounded-lg truncate">
                        {driver.routeCodes?.length
                          ? `Assigned to ${driver.routeCodes.join(", ")}`
                          : "Available"}
                      </p>
                    </div>
                  </div>
                  <button
                    disabled={isDisabled}
                    className={`relative flex items-center justify-center rounded-full   ${
                      isAssigning
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                        : isAssignedToCurrentRoute
                        ? " "
                        : " "
                    }`}
                    onClick={() => handleAssignClick(driver)}
                  >
                    {isAssigning ? (
                      <svg
                        className={`animate-spin h-4 w-4 absolute ${
                          isAssignedToCurrentRoute
                            ? "text-red-500"
                            : "text-amber-600"
                        }`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          className="opacity-25"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : null}
                    <span className={isAssigning ? "invisible" : ""}>
                      {isAssignedToCurrentRoute ? (
                        <button className="px-4 py-1 rounded-full border border-[#FF4848] ">
                          {" "}
                          <img src="\img\user-minus-icon.svg" alt="" />{" "}
                        </button>
                      ) : (
                        <button className="px-4 py-1 rounded-full border border-[#212121] ">
                          {" "}
                          <img src="\img\user-plus-icon.svg" alt="" />{" "}
                        </button>
                      )}
                    </span>
                  </button>
                </div>
              );
            })
          )}
          
        </div>
 
      </div>
    </div>
  );
}